import { Capacitor } from '@capacitor/core'
import capacitorDetectAndroid from './platforms/capacitorDetectAndroid.js'
import capacitorDetectIos from './platforms/capacitorDetectIos.js'
import capacitorDetectPwa from './platforms/capacitorDetectPwa.js'
import cookiesBrowser from './platforms/cookiesBrowser.js'
import cookiesIos from './platforms/cookiesIos.js'

const isVueRouterModeHash = false
const lastSuccessfullyRegisteredPushTokenCookieName = 'saasSocialMatrixPushTokenLastSuccessfulValue'

let cookies = cookiesBrowser
if (Capacitor.getPlatform() === 'ios') {
  cookies = cookiesIos
}

function getPlatform () {
  const isCapacitor = Capacitor.isNativePlatform()
  const platform = Capacitor.getPlatform() // 'ios', 'android', or 'web'

  if (isCapacitor) {
    if (platform === 'android') {
      return 'android'
    } else if (platform === 'ios') {
      return 'ios'
    }
  } else {
    return 'pwa'
  }
}

async function getDevicePwaIsRunningOn () {
  const platform = getPlatform()
  if (platform !== 'pwa') {
    return 'Not PWA - ' + platform
  }
  // Running as PWA — detect underlying OS
  const os = await detectMobileOS()
  if (os === 'android') return 'pwa-android'
  if (os === 'ios') return 'pwa-ios'
  // return 'pwa-ios'
  return 'pwa-desktop'
}

async function detectMobileOS () {
  // Prefer Client Hints where available (Chromium/Android)
  if (navigator.userAgentData) {
    try {
      const hints = await navigator.userAgentData.getHighEntropyValues(['platform'])
      const p = hints.platform.toLowerCase()
      if (p === 'android') return 'android'
      if (['ios', 'iphone', 'ipad'].includes(p)) return 'ios'
    } catch {
      // fall through to UA
    }
  }

  const ua = navigator.userAgent
  if (/android/i.test(ua)) return 'android'
  // iPadOS 13+ spoofs as macOS desktop — touch points expose it
  if (/ipad|iphone|ipod/i.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios'
  }
  return 'unknown'
}

function getPlatformReturnUrl (quasarPath) {
  const platform = getPlatform()
  let pathstart = window.location.protocol + '//' + window.location.host + window.location.pathname
  if (pathstart.endsWith('/')) {
    pathstart = pathstart.slice(0, -1)
  }
  if (platform === 'pwa') {
    if (isVueRouterModeHash) {
      pathstart = window.location.protocol + '//' + window.location.host + window.location.pathname
      return pathstart + '/#' + quasarPath
    }
    // We do not need to add the quasarPath because it is already included in window.location.pathname
    return pathstart
  }
  if (platform === 'android') {
    // if (quasarPath.endsWith('/')) {
    //  quasarPath = quasarPath.slice(0, -1)
    // }
    return 'app.socialclubhub.loginredirect://return/?route=' + quasarPath
  }
  if (platform === 'ios') {
    return 'app.socialclubhub.loginredirect://return/?route=' + quasarPath
  }
}

async function asynccallbackTokenFn ({ tokenValue }) {
  // This is cleared on logout
  cookies.set(lastSuccessfullyRegisteredPushTokenCookieName, tokenValue)
}

async function registerForPushNotifications (matrixClient) {
  const platform = getPlatform()
  if (platform === 'pwa') {
    // PWAs don't have push notifications
    console.log('PWA mode - no push notification')
  } else if (platform === 'android') {
    await capacitorDetectAndroid.registerForPushNotifications(matrixClient, asynccallbackTokenFn)
  } else if (platform === 'ios') {
    await capacitorDetectIos.registerForPushNotifications(matrixClient, asynccallbackTokenFn)
  } else {
    console.log('unknown platform registerForPushNotifications not implemented')
  }
}

async function registerRotatedPushToken ({ matrixClient, tokenValue }) {
  console.log('RJMDEBUG001 registerRotatedPushToken new token')
  // console.log('RJMDEBUG001 registerRotatedPushToken new token', tokenValue)

  let lastRegisteredPushTokenValue
  if (await cookies.has(lastSuccessfullyRegisteredPushTokenCookieName)) {
    lastRegisteredPushTokenValue = await cookies.get(lastSuccessfullyRegisteredPushTokenCookieName)
  }
  if (tokenValue === lastRegisteredPushTokenValue) {
    console.log('RJMDEBUG001 registerPushToken - new token value matches old token')
    return
  }

  const platform = getPlatform()
  if (platform === 'pwa') {
    // PWAs don't have push notifications
    console.log('PWA mode - no push notification')
  } else if (platform === 'android') {
    await capacitorDetectAndroid.registerPushToken({ matrixClient, tokenValue })
  } else if (platform === 'ios') {
    await capacitorDetectIos.registerPushToken({ matrixClient, tokenValue })
  } else {
    console.log('unknown platform registerPushToken not implemented')
  }
  // Save the tokenValue in a cookie so we can not rotate if not nessecary
  await asynccallbackTokenFn({ tokenValue })
}

async function notifyMatrixConnectionDetails (connectionDetails) {
  // Not needed by all types so only send for some - this is called from chatstore
  const platform = getPlatform()
  if (platform === 'android') {
    await capacitorDetectAndroid.notifyMatrixConnectionDetails(connectionDetails)
  } else if (platform === 'ios') {
    await capacitorDetectIos.notifyMatrixConnectionDetails(connectionDetails)
  }
}

async function notifyMatrixLogout (matrixClient) {
  // Not needed by all platforms - this is called from chatstore
  const platform = getPlatform()
  if (platform === 'android') {
    await capacitorDetectAndroid.notifyMatrixLogout(matrixClient)
  } else if (platform === 'ios') {
    await capacitorDetectIos.notifyMatrixLogout(matrixClient)
  }
  await cookies.remove(lastSuccessfullyRegisteredPushTokenCookieName)
}

async function transmitMessage ({ txnId, processData, roomId, messageType, content, matrix, globalEventBus }) {
  // Actual transmission of message. Method depends on platform
  //  matrix is sent in case of local platform
  //  when messages are transmitted the message transmissionManager-messagecomplete is sent on the bus and picked up by this.chatStore.messageTransmissionComplete
  const platform = getPlatform()
  if (platform === 'android') {
    return capacitorDetectAndroid.transmitMessage({ txnId, processData, roomId, messageType, content, matrix, globalEventBus })
  } else if (platform === 'ios') {
    return capacitorDetectIos.transmitMessage({ txnId, processData, roomId, messageType, content, matrix, globalEventBus })
  }
  // fall back to pwa version
  return capacitorDetectPwa.transmitMessage({ txnId, processData, roomId, messageType, content, matrix, globalEventBus })
}

async function handleLoginClick (params) {
  // Return true if window.location.href redirect is required
  const platform = getPlatform()
  if (platform === 'ios') {
    return capacitorDetectIos.handleLoginClick(params)
  }
  return true
}

export default {
  getPlatform,
  getDevicePwaIsRunningOn,
  getPlatformReturnUrl,
  registerForPushNotifications,
  registerRotatedPushToken,
  notifyMatrixConnectionDetails,
  notifyMatrixLogout,
  transmitMessage,
  cookies,
  handleLoginClick
}
